import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HomePage.css';

interface HomePageProps {
  onStartRequest: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ onStartRequest }) => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const featuredDoodles = [
    {
      id: 1,
      title: 'nashville',
      description: 'a scenic shot of the nashville skyline',
      image: '/images/nashville.jpeg',
      rotation: -1.5,
    },
    {
      id: 2,
      title: 'easter bunny',
      description: 'the easter bunny on his annual egg-delivery rounds',
      image: '/images/easter-bunny.jpeg',
      rotation: 2,
    },
    {
      id: 3,
      title: 'unicorn',
      description: 'a majestic talking unicorn',
      image: '/images/unicorn.jpeg',
      rotation: -3,
    },

  ];

  const steps = [
    {
      number: '01',
      title: 'describe it',
      description: 'tell us what you want drawn. an animal, a place, a thing, a pokemon. keep it fun.',
      icon: '✏️',
    },
    {
      number: '02',
      title: 'we draw it',
      description: 'we hand draw your doodle. no AI, no templates, just vibes and a pen.',
      icon: '🎨',
    },
    {
      number: '03',
      title: 'you get it',
      description: 'delivered to your inbox or your mailbox. a custom doodle made for you, totally free.',
      icon: '📬',
    },
  ];

  const miniFaq = [
    {
      q: 'how much does it cost?',
      a: 'nothing, it\'s completely free. we cover everything.',
    },
    {
      q: 'are the doodles any good?',
      a: 'not particularly, but they are real, tangible drawings made for you.',
    },
    {
      q: 'why tho?',
      a: 'in our ever changing post-AI world where it\'s easier than ever to create and generate art, handmade things matter.',
    },
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg-shapes">
          <div className="hero-shape hero-shape-1"></div>
          <div className="hero-shape hero-shape-2"></div>
          <div className="hero-shape hero-shape-3"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge">free & hand-drawn</div>
          <h1 className="hero-title">
            we'll draw you<br />a <span className="hero-highlight">doodle</span>
          </h1>
          <p className="hero-subtitle">
            tell us what you want, and we'll draw it for you
            with a real pen on real paper. then mail it to you.
            for free. because why not.
          </p>
          <div className="hero-actions">
            <button className="cta-button" onClick={onStartRequest}>
              request a doodle
            </button>
            <button className="secondary-button" onClick={() => navigate('/gallery')}>
              see examples
            </button>
          </div>
          <div className="hero-proof">
            <span className="hero-proof-dot"></span>
              every doodle is hand drawn
          </div>
        </div>
        <div className="hero-stack">
          <div className="hero-card hero-card-back-2">
          </div>
          <div className="hero-card hero-card-back-1">
          </div>
          <div className="hero-card hero-card-front">
            <img src="/images/nashville.jpeg" alt="nashville doodle" className="hero-card-img" />
            <div className="hero-card-label">one of our recent deliveries</div>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="gallery-preview-section">
        <h2 className="section-title">some doodles we've made</h2>
        <p className="section-subtitle">sure they're not museum worthy, but they're custom and real which is kinda cool</p>
        <div className="gallery-preview-grid">
          {featuredDoodles.map((doodle) => (
            <div
              key={doodle.id}
              className="preview-card"
              style={{
                '--card-rotation': `${hoveredCard === doodle.id ? 0 : doodle.rotation}deg`,
              } as React.CSSProperties}
              onMouseEnter={() => setHoveredCard(doodle.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="preview-card-image">
                <img src={doodle.image} alt={doodle.title} className="preview-card-img" />
              </div>
              <div className="preview-card-info">
                <h3>{doodle.title}</h3>
                <p>{doodle.description}</p>
              </div>
            </div>
          ))}
        </div>
        <button className="link-button" onClick={() => navigate('/gallery')}>
          see all doodles →
        </button>
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <h2 className="section-title">how it works</h2>
        <p className="section-subtitle">three steps. that's it. we kept it simple.</p>
        <div className="steps-grid">
          {steps.map((step) => (
            <div key={step.number} className="step-card">
              <div className="step-icon">{step.icon}</div>
              <div className="step-number">{step.number}</div>
              <h3 className="step-name">{step.title}</h3>
              <p className="step-desc">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="mini-faq-section">
        <h2 className="section-title">questions you probably have</h2>
        <div className="mini-faq-grid">
          {miniFaq.map((item, i) => (
            <div key={i} className="mini-faq-card">
              <h3 className="mini-faq-q">{item.q}</h3>
              <p className="mini-faq-a">{item.a}</p>
            </div>
          ))}
        </div>
        <button className="link-button" onClick={() => navigate('/faq')}>
          more questions →
        </button>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="final-cta-content">
          <h2>ready for a free doodle?</h2>
          <p>each one is hand-drawn so it may take a bit. but good things are worth waiting for.</p>
          <button className="cta-button" onClick={onStartRequest}>
            request a doodle
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
